




















































import Vue from "vue";
import { DateTime } from "luxon";
import { Availability } from "../types";
import { localTimezone } from "../utils";

export default Vue.extend({
  props: {
    min: {
      type: Object as () => DateTime,
      required: true
    },
    max: {
      type: Object as () => DateTime,
      required: false
    },
    date: {
      type: Object as () => DateTime,
      required: true
    },
    highlightedDates: {
      type: Array as () => DateTime[],
      required: false,
      default: (): DateTime[] => []
    },
    highlightedOnly: {
      type: Boolean,
      required: false,
      default: (): boolean => false
    }
  },

  computed: {
    days(): DateTime[] {
      const luxonDayOfWeekIndex = Number(
        this.date.startOf("month").toFormat("c")
      );
      const daysToPad = luxonDayOfWeekIndex < 7 ? luxonDayOfWeekIndex : 0;
      const start = this.date.startOf("month").minus({ days: daysToPad });
      const weeks = Math.ceil((this.date.daysInMonth + daysToPad) / 7);
      const end = start.plus({ weeks: weeks }).minus({ days: 1 });
      let current = start;
      const days = [];

      while (current <= end) {
        days.push(current);
        current = current.plus({ days: 1 });
      }

      return days;
    },

    monthName(): string {
      return this.date.toFormat("LLLL");
    },

    canGoToPreviousMonth(): boolean {
      return (
        this.date
          .minus({ months: 1 })
          .endOf("month")
          .startOf("day") >= this.min.startOf("day")
      );
    },

    canGoToNextMonth(): boolean {
      if (!this.max) {
        return true;
      }

      return (
        this.date
          .plus({ months: 1 })
          .startOf("month")
          .startOf("day") <= this.max.startOf("day")
      );
    }
  },

  methods: {
    classesFor(date: DateTime): string[] {
      const classes: string[] = [];

      if (date.hasSame(this.date, "day")) {
        // the current day
        return classes.concat("text-white bg-blue-500");
      }

      if (
        !this.canGoToDate(date) ||
        (this.highlightedOnly && !this.isHighlighted(date))
      ) {
        // disabled (cannot navigate here)
        return classes.concat("text-gray-500 pointer-events-none");
      }

      if (!date.hasSame(this.date, "month")) {
        // next or previous month's day, but navigable
        return classes.concat("text-gray-500 hover:text-900 hover:bg-gray-100");
      }

      if (this.isHighlighted(date)) {
        // highlighted due to having availability
        return classes.concat("bg-blue-100 hover:bg-blue-200 hover:text-black");
      }

      // a navigable day in the curent month
      return classes.concat("text-gray-800 hover:text-black hover:bg-blue-100");
    },

    isHighlighted(date: DateTime): boolean {
      return this.highlightedDates.some(
        highlighted =>
          highlighted.setZone(localTimezone).ordinal == date.ordinal
      );
    },

    canGoToDate(date: DateTime) {
      if (date.startOf("day") < this.min.startOf("day")) {
        // too early
        return false;
      }

      if (!this.max) {
        // its ok to go to any future date
        return true;
      }

      return date.startOf("day") <= this.max.startOf("day");
    },

    goToDate(date: DateTime) {
      if (this.canGoToDate(date)) {
        // valid date
        this.$emit("change", date);
      } else if (date.startOf("day") < this.min.startOf("day")) {
        // too early
        this.$emit("change", this.min);
      } else {
        // too far ahead
        this.$emit("change", this.max);
      }
    }
  }
});
